import api from './api';
import {
  StartTestRequest,
  StartTestResponse,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  TestSession,
  TestResult,
  ApiResponse
} from '../types';

class AdaptiveTestingService {
  private readonly baseUrl = '/adaptive-testing';

  /**
   * Start a new adaptive test session
   */
  async startTest(request: StartTestRequest): Promise<StartTestResponse> {
    try {
      const response = await api.post<StartTestResponse>(
        `${this.baseUrl}/start-test`,
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to start test session'
      );
    }
  }

  /**
   * Submit an answer and get the next question
   */
  async submitAnswer(request: SubmitAnswerRequest): Promise<SubmitAnswerResponse> {
    try {
      const response = await api.post<SubmitAnswerResponse>(
        `${this.baseUrl}/submit-answer`,
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to submit answer'
      );
    }
  }

  /**
   * Get test session details
   */
  async getSession(sessionId: string): Promise<TestSession> {
    try {
      const response = await api.get<ApiResponse<{ session: TestSession }>>(
        `${this.baseUrl}/session/${sessionId}`
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error('Session not found');
      }
      
      return response.data.data.session;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to get session details'
      );
    }
  }

  /**
   * Get student's test history with pagination and filtering
   */
  async getStudentHistory(studentId: string, options: {
    page?: number;
    limit?: number;
    subject?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{
    student: any;
    testHistory: TestResult[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      params.append('page', (options.page || 1).toString());
      params.append('limit', (options.limit || 10).toString());
      if (options.subject) params.append('subject', options.subject);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      
      const url = `${this.baseUrl}/student/${studentId}/history?${params.toString()}`;
      console.log('🔍 Making API call to:', url);
      
      const response = await api.get<ApiResponse<{
        student: any;
        testHistory: TestResult[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>>(url);
      
      console.log('✅ API Response received:', response);
      
      if (!response.data.success || !response.data.data) {
        throw new Error('Student history not found');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ API Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config
      });
      
      throw new Error(
        error.response?.data?.error || `Failed to get student history: ${error.message}`
      );
    }
  }

  /**
   * Get system health status
   */
  async getHealthStatus(): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}/health`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to get health status'
      );
    }
  }

  /**
   * Pause a test session
   */
  async pauseSession(sessionId: string): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/session/${sessionId}/pause`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to pause session'
      );
    }
  }

  /**
   * Resume a paused test session
   */
  async resumeSession(sessionId: string): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/session/${sessionId}/resume`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to resume session'
      );
    }
  }

  /**
   * Abandon a test session
   */
  async abandonSession(sessionId: string): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/session/${sessionId}/abandon`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to abandon session'
      );
    }
  }

  /**
   * Get detailed test session with all questions and answers
   */
  async getDetailedSession(sessionId: string): Promise<{
    session: TestSession;
    responses: Array<{
      questionOrder: number;
      question: {
        id: string;
        text: string;
        type: string;
        options: string[];
        topic: string;
        explanation: string;
      };
      studentAnswer: string;
      correctAnswer: string;
      isCorrect: boolean;
      timeTaken: number;
      timeTakenFormatted: string;
      difficultyAtTime: number;
      answeredAt: string;
    }>;
  }> {
    try {
      const response = await api.get<ApiResponse<{
        session: TestSession;
        responses: any[];
      }>>(
        `${this.baseUrl}/session/${sessionId}/detailed`
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error('Detailed session not found');
      }
      
      return response.data.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to get detailed session'
      );
    }
  }

  /**
   * Get student's individual answer history with pagination and filtering
   */
  async getStudentResponses(studentId: string, options: {
    page?: number;
    limit?: number;
    subject?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{
    student: any;
    analytics: {
      totalAnswers: number;
      correctAnswers: number;
      accuracyRate: number;
      averageTime: number;
      fastestAnswer: number;
      slowestAnswer: number;
    } | null;
    responses: Array<{
      sessionId: string;
      session: {
        subject: string;
        grade: number;
        score: number;
        date: string;
      };
      questionOrder: number;
      topic: string;
      questionText: string;
      studentAnswer: string;
      correctAnswer: string;
      isCorrect: boolean;
      timeTaken: number;
      answeredAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      params.append('page', (options.page || 1).toString());
      params.append('limit', (options.limit || 50).toString());
      if (options.subject) params.append('subject', options.subject);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      
      const url = `${this.baseUrl}/student/${studentId}/responses?${params.toString()}`;
      console.log('🔍 Making API call to:', url);
      
      const response = await api.get<ApiResponse<{
        student: any;
        analytics: any;
        responses: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>>(url);
      
      console.log('✅ API Response received:', response);
      
      if (!response.data.success || !response.data.data) {
        throw new Error('Student responses not found');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ API Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config
      });
      
      throw new Error(
        error.response?.data?.error || `Failed to get student responses: ${error.message}`
      );
    }
  }
}

// Export singleton instance
export const adaptiveTestingService = new AdaptiveTestingService();
export default adaptiveTestingService; 